import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@peacecover/component';
import { ArrowLeft, NoticeFill } from '@peacecover/icon';
import { useGetPayLetterCardBlocklist } from '../../../domains/payments/ui/hooks/useGetPayLetterCardBlocklist';
import { useGetPayLetterAvailablePaymentMethods } from '../../../domains/payments/ui/hooks/usegetPayLetterAvailablePaymentMethods';
import {
  PAY_LETTER_PAYMENT_METHOD_OPTIONS,
  type PayLetterPaymentMethodOption
} from '../constants/payLetterPaymentMethods';
import type { PayLetterPaymentMethod } from '../../../domains/orders/services/orderServices';

interface PayLetterPaymentMethodSelectorProps {
  isOpen: boolean;
  isSubmitting?: boolean;
  onClose: () => void;
  onSelect: (paymentMethod: PayLetterPaymentMethod) => void;
}

const PayLetterPaymentMethodSelector = ({
  isOpen,
  isSubmitting = false,
  onClose,
  onSelect
}: PayLetterPaymentMethodSelectorProps) => {
  const { t } = useTranslation();
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<PayLetterPaymentMethodOption | null>(null);

  const { data: payLetterCardBlocklist } = useGetPayLetterCardBlocklist();
  const { data: payLetterAvailablePaymentMethods } = useGetPayLetterAvailablePaymentMethods();

  const availablePaymentMethodOptions = () => {
    if (!payLetterAvailablePaymentMethods || payLetterAvailablePaymentMethods.length === 0) {
      return PAY_LETTER_PAYMENT_METHOD_OPTIONS;
    }

    const optionMap = new Map(
      PAY_LETTER_PAYMENT_METHOD_OPTIONS.map((option) => [option.value, option])
    );

    return payLetterAvailablePaymentMethods
      .map((method) => {
        const matchedOption = optionMap.get(method.code);
        if (!matchedOption) return null;
        return {
          ...matchedOption,
          label: method.name || matchedOption.label
        };
      })
      .filter((method): method is PayLetterPaymentMethodOption => method !== null);
  };

  const blockCardList =
    payLetterCardBlocklist && payLetterCardBlocklist.length > 0
      ? payLetterCardBlocklist?.map((card) => card.name)
      : [];

  if (!isOpen) return null;
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div className="fixed inset-0 z-[20000] bg-background-light dark:bg-background-dark pointer-events-auto pt-[env(safe-area-inset-top)]">
      <div className="px-[16px] py-[10px] flex items-center">
        <button type="button" onClick={onClose}>
          <ArrowLeft className="text-text-main-lv1" />
        </button>
      </div>
      <div className="flex flex-col items-center gap-[14px] px-[24px] py-[10px] mt-[78px]">
        <p className="text-[26px] font-bold text-text-main-lv1">
          {t('BettieChargeBottomSheet.selectMethod')}
        </p>
        <p className="text-[15px] leading-[18px] text-text-main-lv4">
          {t('BettieChargeBottomSheet.selectMethodTitle')}
        </p>
      </div>
      <div className="px-[24px] mt-[39px]">
        {blockCardList.length > 0 && (
          <div className="flex items-center gap-[7px] pl-[10px] pt-[9px] pb-[10px] w-full rounded-[12px] bg-separator1 shadow-[0_0_2px_0_rgba(0,0,0,0.05),0_0_8px_0_rgba(0,0,0,0.05)]">
            <NoticeFill size={13} className="text-text-main-lv4" />
            <span className="text-[12px] font-medium leading-[14px] text-text-main-lv4">
              {t('BettieChargeBottomSheet.cannotUseCard', { cardList: blockCardList.join(', ') })}
            </span>
          </div>
        )}
      </div>
      <div className="grid grid-cols-3 gap-[10px] px-[24px] mt-[10px]">
        {availablePaymentMethodOptions().map((method) => (
          <button
            key={method.value}
            type="button"
            className={`w-full min-w-[102px] min-h-[85px] rounded-[12px] px-[10px] py-[18px] shadow-[0_0_2px_0_rgba(0,0,0,0.05),0_0_8px_0_rgba(0,0,0,0.05)] ${selectedPaymentMethod?.value === method.value ? 'ring-[1.5px] ring-inset ring-primary-light dark:ring-primary-dark bg-secondary-light dark:bg-secondary-dark' : 'ring-[0.5px] ring-inset ring-separator2'}`}
            disabled={isSubmitting}
            onClick={() => setSelectedPaymentMethod(method)}
          >
            <div className="flex flex-col items-center gap-[10px]">
              <img
                src={method.imgSrc}
                alt={method.label}
                className="w-[52px] h-[25px] rounded-[3px]"
              />
              <span className="text-[12px] font-medium leading-[14px] text-text-main-lv4">{method.label}</span>
            </div>
          </button>
        ))}
      </div>
      <div className="fixed left-0 right-0 px-[24px] pb-[calc(env(safe-area-inset-bottom)+10px)] bottom-0">
        <Button
          size="large"
          className="w-full text-[17px] font-semibold rounded-[18px]"
          disabled={!selectedPaymentMethod}
          onClick={() => selectedPaymentMethod && onSelect(selectedPaymentMethod.value)}
        >
          {selectedPaymentMethod
            ? t('BettieChargeBottomSheet.payButton', { method: selectedPaymentMethod.label })
            : t('next')}
        </Button>
      </div>
    </div>,
    document.body
  );
};

export default PayLetterPaymentMethodSelector;
